import { useState, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Row,
  Col,
  Card,
  Button,
  Input,
  Select,
  Tag,
  Typography,
  Spin,
  Empty,
  Modal,
  Form,
  Tabs,
  Statistic,
  Tooltip,
  message,
  Popconfirm,
  Badge,
  Divider,
} from "antd";
import {
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  SearchOutlined,
  BookOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import { api } from "@/services/api";
import { API_ENDPOINTS, DEBOUNCE_DELAY } from "@/constants";
import type {
  LibraryQuestion,
  LibraryStats,
  LibraryListResponse,
  LibraryQuestionStatus,
  Board,
  ApiResponse,
} from "@/types";

interface BoardWithSubjects extends Board {
  subjects: { id: string; name: string; colorHex: string | null }[];
}

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const STATUS_COLORS: Record<LibraryQuestionStatus, string> = {
  PENDING: "orange",
  APPROVED: "green",
  REJECTED: "red",
};

const DIFFICULTY_COLORS = {
  EASY: "#22c55e",
  MEDIUM: "#f59e0b",
  HARD: "#ef4444",
};

export default function QuestionLibraryPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string>("ALL");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [subjectFilter, setSubjectFilter] = useState<string | undefined>();
  const [boardPageFilter, setBoardPageFilter] = useState<string | undefined>();
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [rejectModalId, setRejectModalId] = useState<string | null>(null);
  const [editQuestion, setEditQuestion] = useState<LibraryQuestion | null>(
    null,
  );
  const [createForm] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [formBoardId, setFormBoardId] = useState<string | undefined>();

  // ── Boards + subjects ────────────────────────────────────────────────────
  const { data: boardsData } = useQuery<ApiResponse<BoardWithSubjects[]>>({
    queryKey: ["curriculum-boards"],
    queryFn: () => api.get(API_ENDPOINTS.CURRICULUM_BOARDS),
    staleTime: 5 * 60 * 1000,
  });
  const boards = boardsData?.data ?? [];
  const allSubjects = boards.flatMap((b) => b.subjects);
  const formSubjects = boards.find((b) => b.id === formBoardId)?.subjects ?? [];

  // ── Stats ────────────────────────────────────────────────────────────────
  const { data: statsData } = useQuery<ApiResponse<LibraryStats>>({
    queryKey: ["admin-library-stats"],
    queryFn: () => api.get(API_ENDPOINTS.ADMIN_LIBRARY_STATS),
  });
  const stats = statsData?.data;

  // ── Questions list ───────────────────────────────────────────────────────
  const statusParam =
    activeTab === "ALL" ? undefined : (activeTab as LibraryQuestionStatus);
  const { data: listData, isLoading } = useQuery<
    ApiResponse<LibraryListResponse>
  >({
    queryKey: [
      "admin-library-questions",
      statusParam,
      subjectFilter,
      debouncedSearch,
      cursor,
    ],
    queryFn: () =>
      api.get(API_ENDPOINTS.ADMIN_LIBRARY_QUESTIONS, {
        params: {
          ...(statusParam && { status: statusParam }),
          ...(subjectFilter && { subjectId: subjectFilter }),
          ...(debouncedSearch && { search: debouncedSearch }),
          ...(cursor && { cursor }),
          limit: 20,
        },
      }),
  });
  const questions = listData?.data?.items ?? [];
  const meta = listData?.data?.meta;
  const total = (meta as unknown as Record<string, unknown>)?.total as number ?? 0;
  const currentPage = cursorStack.length + 1;

  const handleSearchChange = useCallback((val: string) => {
    setSearch(val);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
      setCursor(undefined);
      setCursorStack([]);
    }, DEBOUNCE_DELAY);
  }, []);

  const handleNextPage = () => {
    if (meta?.nextCursor) {
      setCursorStack((prev) => [...prev, cursor ?? ""]);
      setCursor(meta.nextCursor);
    }
  };

  const handlePrevPage = () => {
    const stack = [...cursorStack];
    const prev = stack.pop();
    setCursorStack(stack);
    setCursor(prev || undefined);
  };

  // ── Mutations ────────────────────────────────────────────────────────────
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-library-questions"] });
    queryClient.invalidateQueries({ queryKey: ["admin-library-stats"] });
  };

  const approveMutation = useMutation({
    mutationFn: (id: string) =>
      api.post(`${API_ENDPOINTS.ADMIN_LIBRARY_QUESTIONS}/${id}/approve`),
    onSuccess: () => {
      message.success("Question approved");
      invalidateAll();
    },
    onError: () => message.error("Failed to approve question"),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`${API_ENDPOINTS.ADMIN_LIBRARY_QUESTIONS}/${id}/reject`, {
        reason,
      }),
    onSuccess: () => {
      message.success("Question rejected");
      setRejectModalId(null);
      rejectForm.resetFields();
      invalidateAll();
    },
    onError: () => message.error("Failed to reject question"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      api.delete(`${API_ENDPOINTS.ADMIN_LIBRARY_QUESTIONS}/${id}`),
    onSuccess: () => {
      message.success("Question deleted");
      invalidateAll();
    },
    onError: () => message.error("Failed to delete question"),
  });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post(API_ENDPOINTS.ADMIN_LIBRARY_QUESTIONS, data),
    onSuccess: () => {
      message.success("Question added to library");
      setCreateOpen(false);
      createForm.resetFields();
      setFormBoardId(undefined);
      invalidateAll();
    },
    onError: () => message.error("Failed to create question"),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch(`${API_ENDPOINTS.ADMIN_LIBRARY_QUESTIONS}/${id}`, data),
    onSuccess: () => {
      message.success("Question updated");
      setEditQuestion(null);
      editForm.resetFields();
      invalidateAll();
    },
    onError: () => message.error("Failed to update question"),
  });

  const openEdit = (q: LibraryQuestion) => {
    const boardId = q.subjectId
      ? boards.find((b) => b.subjects.some((s) => s.id === q.subjectId))?.id
      : undefined;
    setFormBoardId(boardId);
    setEditQuestion(q);
    editForm.setFieldsValue({
      boardId: boardId ?? undefined,
      title: q.title ?? "",
      frontText: q.frontText,
      backText: q.backText ?? "",
      tags: q.tags,
      difficulty: q.difficulty,
      subjectId: q.subjectId ?? undefined,
    });
  };

  // ── Question card component ───────────────────────────────────────────────
  const QuestionCard = ({ q }: { q: LibraryQuestion }) => (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        marginBottom: 12,
        overflow: "hidden",
      }}
    >
      {/* Main body */}
      <div style={{ padding: "16px 20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          {/* Left: content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {q.title && (
              <Text
                strong
                style={{
                  display: "block",
                  fontSize: 14,
                  color: "#0f172a",
                  marginBottom: 4,
                }}
              >
                {q.title}
              </Text>
            )}
            <Paragraph
              ellipsis={{ rows: 2 }}
              style={{ margin: 0, fontSize: 13, color: "#334155" }}
            >
              {q.frontText}
            </Paragraph>
            {q.backText && (
              <Text
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  display: "block",
                  marginTop: 4,
                }}
              >
                Answer: {q.backText.slice(0, 100)}
                {q.backText.length > 100 ? "…" : ""}
              </Text>
            )}
          </div>
          {/* Right: badges + actions */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 8,
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", gap: 6 }}>
              <Tag
                color={STATUS_COLORS[q.status]}
                style={{ margin: 0, fontWeight: 600 }}
              >
                {q.status}
              </Tag>
              <Tag
                style={{
                  margin: 0,
                  fontWeight: 600,
                  fontSize: 11,
                  color: DIFFICULTY_COLORS[q.difficulty],
                  border: `1px solid ${DIFFICULTY_COLORS[q.difficulty]}`,
                  background: `${DIFFICULTY_COLORS[q.difficulty]}18`,
                }}
              >
                {q.difficulty}
              </Tag>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {q.status === "PENDING" && (
                <Tooltip title="Approve">
                  <Button
                    size="small"
                    type="text"
                    icon={<CheckOutlined />}
                    style={{ color: "#22c55e" }}
                    loading={approveMutation.isPending}
                    onClick={() => approveMutation.mutate(q.id)}
                  />
                </Tooltip>
              )}
              {q.status === "PENDING" && (
                <Tooltip title="Reject">
                  <Button
                    size="small"
                    type="text"
                    icon={<CloseOutlined />}
                    style={{ color: "#ef4444" }}
                    onClick={() => {
                      setRejectModalId(q.id);
                      rejectForm.resetFields();
                    }}
                  />
                </Tooltip>
              )}
              <Tooltip title="Edit">
                <Button
                  size="small"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => openEdit(q)}
                />
              </Tooltip>
              <Popconfirm
                title="Delete this question?"
                description="This cannot be undone."
                onConfirm={() => deleteMutation.mutate(q.id)}
                okText="Delete"
                okButtonProps={{ danger: true }}
              >
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </div>
          </div>
        </div>

        {/* Tags row */}
        {(q.subject || q.chapter || q.tags.length > 0) && (
          <div
            style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 10 }}
          >
            {q.subject && (
              <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
                {q.subject.name}
              </Tag>
            )}
            {q.chapter && (
              <Tag color="geekblue" style={{ fontSize: 11, margin: 0 }}>
                {q.chapter.name}
              </Tag>
            )}
            {q.tags.map((t) => (
              <Tag
                key={t}
                icon={<TagsOutlined />}
                style={{ fontSize: 11, margin: 0 }}
              >
                {t}
              </Tag>
            ))}
          </div>
        )}

        {/* Rejection reason */}
        {q.status === "REJECTED" && q.rejectionReason && (
          <div
            style={{
              marginTop: 10,
              padding: "6px 10px",
              background: "#fef2f2",
              borderRadius: 6,
              fontSize: 12,
              color: "#dc2626",
              borderLeft: "3px solid #ef4444",
            }}
          >
            {q.rejectionReason}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "8px 20px",
          background: "#f8fafc",
          borderTop: "1px solid #f1f5f9",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Text style={{ fontSize: 11, color: "#94a3b8" }}>
          By {q.createdBy?.name} · {q.createdBy?.role}
        </Text>
        <Text style={{ fontSize: 11, color: "#94a3b8" }}>
          Used {q.usageCount}×
        </Text>
      </div>
    </div>
  );

  // ── Question form fields (reused for create/edit) ────────────────────────
  const QuestionFormFields = ({ form }: { form: ReturnType<typeof Form.useForm>[0] }) => (
    <>
      <Form.Item name="title" label="Title (optional)" rules={[{ max: 50 }]}>
        <Input placeholder="Short title" maxLength={50} />
      </Form.Item>
      <Form.Item
        name="frontText"
        label="Question / Front"
        rules={[{ required: true, message: "Front text is required" }, { max: 500 }]}
      >
        <TextArea rows={3} placeholder="What is Newton's First Law?" maxLength={500} showCount />
      </Form.Item>
      <Form.Item name="backText" label="Answer / Back (optional)" rules={[{ max: 500 }]}>
        <TextArea rows={2} placeholder="An object at rest stays at rest..." maxLength={500} showCount />
      </Form.Item>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="boardId" label="Board">
            <Select
              allowClear
              placeholder="Select board"
              onChange={(v) => {
                setFormBoardId(v);
                form.setFieldValue("subjectId", undefined);
              }}
            >
              {boards.map((b) => (
                <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="difficulty" label="Difficulty" initialValue="MEDIUM">
            <Select>
              <Select.Option value="EASY">Easy</Select.Option>
              <Select.Option value="MEDIUM">Medium</Select.Option>
              <Select.Option value="HARD">Hard</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="subjectId" label="Subject">
        <Select
          allowClear
          disabled={!formBoardId}
          placeholder={formBoardId ? "Select subject" : "Select board first"}
        >
          {(formBoardId ? formSubjects : allSubjects).map((s) => (
            <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item name="tags" label="Tags (up to 10)">
        <Select
          mode="tags"
          placeholder="Type tag and press Enter"
          maxCount={10}
          tokenSeparators={[","]}
        />
      </Form.Item>
    </>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Question Library
          </Title>
          <Text style={{ color: "#64748b", fontSize: 13 }}>
            Manage shared questions that teachers can import into their decks
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
        >
          Add Question
        </Button>
      </div>

      {/* Stats row */}
      {stats && (
        <Row gutter={12} style={{ marginBottom: 20 }}>
          {[
            { label: "Total", value: stats.total, color: "#6366f1" },
            { label: "Pending Review", value: stats.pending, color: "#f59e0b" },
            { label: "Approved", value: stats.approved, color: "#22c55e" },
            { label: "Rejected", value: stats.rejected, color: "#ef4444" },
          ].map(({ label, value, color }) => (
            <Col span={6} key={label}>
              <Card size="small" style={{ borderRadius: 10, textAlign: "center" }}>
                <Statistic
                  title={<span style={{ fontSize: 12, color: "#64748b" }}>{label}</span>}
                  value={value}
                  valueStyle={{ color, fontSize: 22, fontWeight: 700 }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Filters */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Input
            placeholder="Search questions..."
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            allowClear
            style={{
              flex: "1 1 180px",
              height: 32,
            }}
          />
        </Col>
        <Col span={8}>
          <Select
            allowClear
            placeholder="Filter by board"
            style={{ width: "100%" }}
            value={boardPageFilter}
            onChange={(v) => {
              setBoardPageFilter(v);
              setSubjectFilter(undefined);
              setCursor(undefined);
              setCursorStack([]);
            }}
          >
            {boards.map((b) => (
              <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>
            ))}
          </Select>
        </Col>
        <Col span={8}>
          <Select
            allowClear
            placeholder={boardPageFilter ? "Filter by subject" : "Select board first"}
            disabled={!boardPageFilter}
            style={{ width: "100%" }}
            value={subjectFilter}
            onChange={(v) => {
              setSubjectFilter(v);
              setCursor(undefined);
              setCursorStack([]);
            }}
          >
            {(boards.find((b) => b.id === boardPageFilter)?.subjects ?? []).map((s) => (
              <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      {/* Status tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={(k) => {
          setActiveTab(k);
          setCursor(undefined);
          setCursorStack([]);
        }}
        style={{ marginBottom: 16 }}
        items={[
          { key: "ALL", label: "All" },
          {
            key: "PENDING",
            label: (
              <span>
                Pending{" "}
                {stats?.pending ? (
                  <Badge count={stats.pending} style={{ backgroundColor: "#f59e0b" }} />
                ) : null}
              </span>
            ),
          },
          { key: "APPROVED", label: "Approved" },
          { key: "REJECTED", label: "Rejected" },
        ]}
      />

      {/* Questions list */}
      {isLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : questions.length === 0 ? (
        <Empty
          description="No questions found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <>
          {questions.map((q) => (
            <QuestionCard key={q.id} q={q} />
          ))}

          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: "#64748b", marginRight: 8 }}>Page {currentPage}{total > 0 && <> &middot; {total.toLocaleString()} question{total !== 1 ? 's' : ''}</>}</span>
            <Button
              size="small"
              disabled={cursorStack.length === 0}
              onClick={handlePrevPage}
            >
              Previous
            </Button>
            <Button
              size="small"
              disabled={!meta?.hasMore}
              onClick={handleNextPage}
            >
              Next
            </Button>
          </div>
        </>
      )}

      {/* Create modal */}
      <Modal
        title={
          <>
            <BookOutlined /> Add Library Question
          </>
        }
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          createForm.resetFields();
          setFormBoardId(undefined);
        }}
        onOk={() =>
          createForm.validateFields().then((values) => {
            const { boardId: _boardId, ...payload } = values;
            createMutation.mutate({ ...payload, tags: payload.tags ?? [] });
          })
        }
        okText="Add to Library"
        confirmLoading={createMutation.isPending}
        width={600}
        destroyOnHidden
      >
        <Divider style={{ margin: "12px 0" }} />
        <Form form={createForm} layout="vertical" requiredMark={false}>
          <QuestionFormFields form={createForm} />
        </Form>
      </Modal>

      {/* Edit modal */}
      <Modal
        title={
          <>
            <EditOutlined /> Edit Library Question
          </>
        }
        open={!!editQuestion}
        onCancel={() => {
          setEditQuestion(null);
          editForm.resetFields();
          setFormBoardId(undefined);
        }}
        onOk={() =>
          editForm.validateFields().then((values) => {
            const { boardId: _boardId, ...payload } = values;
            editMutation.mutate({
              id: editQuestion!.id,
              data: { ...payload, tags: payload.tags ?? [] },
            });
          })
        }
        okText="Save Changes"
        confirmLoading={editMutation.isPending}
        width={600}
        destroyOnHidden
      >
        <Divider style={{ margin: "12px 0" }} />
        <Form form={editForm} layout="vertical" requiredMark={false}>
          <QuestionFormFields form={editForm} />
        </Form>
      </Modal>

      {/* Reject modal */}
      <Modal
        title={
          <>
            <ExclamationCircleOutlined style={{ color: "#ef4444" }} /> Reject
            Question
          </>
        }
        open={!!rejectModalId}
        onCancel={() => {
          setRejectModalId(null);
          rejectForm.resetFields();
        }}
        onOk={() =>
          rejectForm.validateFields().then((values) => {
            rejectMutation.mutate({
              id: rejectModalId!,
              reason: values.reason,
            });
          })
        }
        okText="Reject"
        okButtonProps={{ danger: true }}
        confirmLoading={rejectMutation.isPending}
        destroyOnHidden
      >
        <Form form={rejectForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="reason"
            label="Reason for rejection (shown to the submitter)"
            rules={[{ required: true, message: "Please provide a reason" }, { max: 500 }]}
          >
            <TextArea rows={3} maxLength={500} showCount placeholder="E.g. Content is too vague, please add more detail." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
